import React, { useState, useEffect } from 'react';
import { Form, Col, Row, Alert, Navbar, Nav } from 'react-bootstrap';
import './PromotionProducts.scss';
import { AppState } from 'reducers';
import { useSelector } from 'react-redux';
import { PromotionalProduct } from 'lib/types';
import _ from 'lodash';
import Spinner from 'components/Spinner';
import ScrollUpButton from 'components/ScrollUpButton';
import PromotionProductsListDesktop from './PromotionProductsListDesktop';
import PromotionProductsListMobile from './PromotionProductsListMobile';

import { useHistory } from 'react-router-dom';
import { TiArrowRightThick } from 'react-icons/ti';
import { FaRedo } from 'react-icons/fa';
import NumberInput from 'components/NumberInput';
import ButtonComponent from 'components/ButtonComponent';

interface IPromotionProductsListProps {
    error: boolean;
    isDesktop: boolean;
    remainingPoints: number;
    selectedCount: Map<PromotionalProduct, number[]>;
    setShowSummary: (_: boolean) => void;
    setSelectedCount: React.Dispatch<React.SetStateAction<Map<PromotionalProduct, number[]>>>;
}

const PromotionProductsList = ({ error, isDesktop, remainingPoints, selectedCount, setShowSummary, setSelectedCount }: IPromotionProductsListProps) => {
    const history = useHistory();

    const promotionalProducts = useSelector((state: AppState) => state.data.promotionalProgram.products);
    const isFetchingProducts = useSelector((state: AppState) => state.data.promotionalProgram.props.isFetchingProducts);

    const grandTotalPoints = Array.from(selectedCount.entries())
        .reduce((previous, entity) => previous + entity[1].reduce((prev, curr) => prev + curr) * entity[0].points, 0);

    const [items, setItems] = useState([] as PromotionalProduct[]);
    const [productGroup, setProductGroup] = useState(history.location.state as string || 'Wszystkie');

    useEffect(() => {
        const filteredProducts = _.filter(promotionalProducts, (product) => product.group === productGroup || productGroup === 'Wszystkie');
        setItems(filteredProducts);
    }, [promotionalProducts, productGroup]);

    const handleCountChange = (value: number, product: PromotionalProduct, option: number = 0) => {
        setSelectedCount((prev) => {
            const map = new Map(prev);
            map.get(product)![option] = value;
            return map;
        });
    };

    const emptyInputFields = () => {
        _.forEach(items, (product) => {
            _.each(product.options, (option, optionIndex) => {
                handleCountChange(0, product, optionIndex);
            });
        });
    };

    const makeCountInputs = (product: PromotionalProduct) => _.map(product.options, (option, optionIndex) => (
        <Form.Group
            as={Row}
            noGutters
            className='PromotionProducts-variant ml-auto'
            controlId={`product-count-${product.symKar}${option}`}
            key={`${optionIndex}-${option}`}
        >
            <Form.Label column className='pr-1'>
                {option}
            </Form.Label>
            <Col>
                <NumberInput
                    className='ml-auto justify-content-end'
                    value={selectedCount.get(product) ? selectedCount.get(product)![optionIndex] : 0}
                    min={0}
                    setValue={(value: number) => handleCountChange(value, product, optionIndex)}
                    stopPropagation={!isDesktop}
                    disabledEdit={selectedCount.get(product) ? (selectedCount.get(product)![optionIndex] === 0) : false}
                />
            </Col>
        </Form.Group>
    ));

    useEffect(() => {
        (document?.activeElement as HTMLElement).blur();
        const group = history.location.state ?? 'Wszystkie';
        setProductGroup(group as string);
    }, [history.location.state]);

    return (
        <>
            <Form onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
                event.preventDefault();
                setShowSummary(true);
                event.currentTarget.scrollIntoView({ behavior: 'smooth' });
            }}
            >
                <h3>Artykuły promocyjne</h3>
                {
                    (isFetchingProducts || error) ? (
                        <Spinner centered={false} showError={error} />
                    ) : (
                        <>
                            <Navbar
                                className='sub-navbar'
                                expand='lg'
                                onSelect={(eventKey) => {
                                    history.push(history.location.pathname, eventKey);
                                }}
                            >
                                <Nav variant='pills' fill justify className='flex-grow-1 d-flex flex-wrap' activeKey={productGroup}>
                                    <Nav.Item key='Wszystkie'>
                                        <Nav.Link eventKey='Wszystkie'>Wszystkie</Nav.Link>
                                    </Nav.Item>
                                    {
                                        _.uniq(_.map(promotionalProducts, 'group')).map((value) => (
                                            <Nav.Item key={value}>
                                                <Nav.Link eventKey={value}>{value}</Nav.Link>
                                            </Nav.Item>
                                        ))
                                    }
                                </Nav>
                            </Navbar>
                            {
                                isDesktop ? (
                                    <PromotionProductsListDesktop
                                        items={items}
                                        selectedCount={selectedCount}
                                        makeCountInputs={makeCountInputs}
                                    />
                                ) : (
                                    <PromotionProductsListMobile
                                        items={items}
                                        selectedCount={selectedCount}
                                        makeCountInputs={makeCountInputs}
                                    />
                                )
                            }
                            <div className='PromotionProducts-summary-box'>
                                <div className='PromotionProducts-summary-top'>
                                    <ScrollUpButton />
                                    {
                                        remainingPoints < 0 && (
                                            <Alert variant='danger' className='PromotionProducts-points-warning'>
                                                Niewystarczająca ilość punktów.
                                            </Alert>
                                        )
                                    }
                                </div>
                                {
                                    grandTotalPoints > 0 && (
                                        <div>
                                            <div className='PromotionProducts-summary-content'>
                                                <ButtonComponent
                                                    variant='dark'
                                                    brandColoredButton={false}
                                                    onClick={emptyInputFields}
                                                    width={isDesktop ? 'auto' : '48px'}
                                                    height={isDesktop ? '42px' : '48px'}
                                                    padding={isDesktop ? '0.05rem 0.75rem' : '0'}
                                                    fontSize={isDesktop ? undefined : '150%'}
                                                >
                                                    {isDesktop ? 'Resetuj zamówienie' : <FaRedo style={{ transform: 'scale(-1, 1)' }} />}
                                                </ButtonComponent>

                                                <div>
                                                    <div className='PromotionProducts-summary-points-title'>Koszt zamówienia</div>
                                                    <div>{grandTotalPoints}</div>
                                                </div>

                                                <div>
                                                    <div className='PromotionProducts-summary-points-title'>Pozostanie punktów</div>
                                                    <div className={remainingPoints < 0 ? 'text-danger' : ''}>{remainingPoints}</div>
                                                </div>

                                                <ButtonComponent
                                                    variant='dark'
                                                    brandColoredButton={false}
                                                    type='submit'
                                                    disabled={_.every(
                                                        _.map(Array.from(selectedCount.values()), (sc) => _.every(sc, (c) => c === 0)),
                                                        (res) => res === true
                                                    ) || remainingPoints < 0}
                                                    width={isDesktop ? 'auto' : '48px'}
                                                    height={isDesktop ? '42px' : '48px'}
                                                    padding={isDesktop ? '0.05rem 0.75rem' : '0'}
                                                    fontSize={isDesktop ? undefined : '150%'}
                                                >
                                                    {isDesktop ? 'Przejdź do podsumowania' : <TiArrowRightThick />}
                                                </ButtonComponent>

                                            </div>
                                        </div>
                                    )
                                }
                            </div>
                        </>
                    )
                }
            </Form>
        </>
    );
};

export default PromotionProductsList;
